import {
	AlternateEmail as AlternateEmailIcon,
	Assignment as AssignmentIcon,
	Build as BuildIcon,
	Chat as ChatIcon,
	ChevronLeft as ChevronLeftIcon,
	Code as CodeIcon,
	Email as EmailIcon,
	Group as GroupIcon,
	Memory as MemoryIcon,
	Menu as MenuIcon,
	People as PeopleIcon,
	Settings as SettingsIcon,
	Storage as StorageIcon,
	Timeline as TimelineIcon,
	ViewList as ViewListIcon,
} from "@mui/icons-material";
import {
	Divider,
	Drawer,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import React, {
	FC,
	ReactElement,
	useContext,
	useEffect,
	useState,
} from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";

import Asset from "./configuration/pages/asset";
import ClientTelemetry from "./configuration/pages/clientTelemetry";
import EmailAddresses from "./configuration/pages/emailAddresses";
import EmailTopics from "./configuration/pages/emailTopics";
import Jobs from "./configuration/pages/job";
import Logs from "./configuration/pages/log";
import Operators from "./configuration/pages/operators";
import Profiles from "./configuration/pages/profiles";
import QueryCachePage from "./configuration/pages/query-cache";
import ChatComponent from "./configuration/pages/realtime/client";
import QueryCacheInvalidations from "./configuration/pages/realtime/config";
import Settings from "./configuration/pages/settings";
import ShiftsCrews from "./configuration/pages/shifCrew";
import Users from "./configuration/pages/users";
import {
	ConfigurationContext,
	EmailTopicsFeatureContext,
	OperatorsFeatureContext,
} from "./globalContext";
import { SPExecutorPage } from "./pages/sp-executor";

export const emailTopicsUrl = "/configuration/email-topics";

export const MESF_CONFIGURATIONS = [
	"shifts",
	"asset",
	"operators",
	"users",
	"profiles",
	"logs",
	"client-telemetry",
	"jobs",
	"settings",
	"email-topics",
	"email-addresses",
	"real-time",
	"query-cache",
	"sp-executor",
];

interface SectionComponent {
	path: string;
	sidebar: FC;
	main: FC;
	exact?: boolean;
}

export type SidebarType = FC<{ option?: string }>;

export type ConfigurationsType = [SectionComponent[], SidebarType];

const ConfigurationRootRedirect: FC = () => <Navigate to="users" />;

const Configuration: FC = (): ReactElement => {
	const { option } = useParams();

	const [customConfiguration, CustomSidebar] =
		useContext<ConfigurationsType>(ConfigurationContext);

	const operatorsFeature = useContext<boolean>(OperatorsFeatureContext);

	const emailTopicsFeature = useContext<boolean>(EmailTopicsFeatureContext);

	const routes: Array<SectionComponent> = [
		{
			path: "/",
			sidebar: (): ReactElement => <div>Configuration</div>,
			main: ConfigurationRootRedirect,
		},
		{
			path: "shifts",
			sidebar: (): ReactElement => <div>Shifts</div>,
			main: ShiftsCrews,
		},
		{
			path: "asset",
			sidebar: (): ReactElement => <div>Configuration Asset</div>,
			main: Asset,
		},
		...(operatorsFeature
			? [
					{
						path: "operators/*",
						sidebar: (): ReactElement => <div>Operators</div>,
						main: Operators,
					},
				]
			: []),
		{
			path: "users",
			sidebar: (): ReactElement => <div>Users</div>,
			main: Users,
		},
		{
			path: "profiles",
			sidebar: (): ReactElement => <div>profiles</div>,
			main: Profiles,
		},
		{
			path: "logs",
			sidebar: (): ReactElement => <div>Logs</div>,
			main: Logs,
		},
		{
			path: "client-telemetry",
			sidebar: (): ReactElement => <div>Client Telemetry</div>,
			main: ClientTelemetry,
		},
		{
			path: "jobs",
			sidebar: (): ReactElement => <div>Jobs</div>,
			main: Jobs,
		},
		{
			path: "settings",
			sidebar: (): ReactElement => <div>Settings</div>,
			main: Settings,
		},
		...(emailTopicsFeature
			? [
					{
						path: "email-topics/*",
						sidebar: (): ReactElement => <div>Email Topics</div>,
						main: EmailTopics,
					},
					{
						path: "email-addresses/*",
						sidebar: (): ReactElement => <div>Email Addresses</div>,
						main: EmailAddresses,
					},
				]
			: []),
		{
			path: "real-time/client",
			sidebar: (): ReactElement => <div>Client</div>,
			main: ChatComponent,
		},
		{
			path: "real-time/config",
			sidebar: (): ReactElement => <div>Config</div>,
			main: QueryCacheInvalidations,
		},
		{
			path: "query-cache/config",
			sidebar: (): ReactElement => <div>Config</div>,
			main: QueryCachePage,
		},
		{
			path: "sp-executor",
			sidebar: (): ReactElement => <div>SP Executor</div>,
			main: SPExecutorPage,
		},
		...customConfiguration,
	];

	// At the top level of your component, add a useEffect to handle window resizing
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [drawerOpen, setDrawerOpen] = useState(!isMobile);

	// Add useEffect to handle drawer state on screen resize
	React.useEffect(() => {
		setDrawerOpen(!isMobile);
	}, [isMobile]);

	const renderDrawerContent = () => (
		<>
			<Typography
				variant="h6"
				style={{
					padding: theme.spacing(2, 2, 1),
					color: theme.palette.primary.main,
				}}
			>
				Configuration
			</Typography>
			<Divider />

			<List
				subheader={
					<ListSubheader
						style={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.primary.main,
							fontWeight: 600,
						}}
					>
						Plant
					</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "shifts"}
					component={Link}
					to="/configuration/shifts"
				>
					<ListItemIcon>
						<PeopleIcon />
					</ListItemIcon>
					<ListItemText primary="Shift / Crews" />
				</ListItemButton>
				<ListItemButton
					selected={option === "asset"}
					component={Link}
					to="/configuration/asset"
				>
					<ListItemIcon>
						<StorageIcon />
					</ListItemIcon>
					<ListItemText primary="Assets Tree" />
				</ListItemButton>
			</List>

			<Divider />

			<List
				subheader={
					<ListSubheader
						style={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.primary.main,
							fontWeight: 600,
						}}
					>
						Security
					</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "users"}
					component={Link}
					to="/configuration/users"
				>
					<ListItemIcon>
						<GroupIcon />
					</ListItemIcon>
					<ListItemText primary="Users" />
				</ListItemButton>
				<ListItemButton
					selected={option === "profiles"}
					component={Link}
					to="/configuration/profiles"
				>
					<ListItemIcon>
						<AssignmentIcon />
					</ListItemIcon>
					<ListItemText primary="Profiles" />
				</ListItemButton>
			</List>

			{operatorsFeature && (
				<>
					<Divider />

					<List
						subheader={
							<ListSubheader
								style={{
									backgroundColor: theme.palette.background.paper,
									color: theme.palette.primary.main,
									fontWeight: 600,
								}}
							>
								Operators
							</ListSubheader>
						}
					>
						<ListItemButton
							selected={option === "operators"}
							component={Link}
							to="/configuration/operators"
						>
							<ListItemIcon>
								<PeopleIcon />
							</ListItemIcon>
							<ListItemText primary="Operators" />
						</ListItemButton>
					</List>
				</>
			)}

			{emailTopicsFeature && (
				<>
					<Divider />

					<List
						subheader={
							<ListSubheader
								style={{
									backgroundColor: theme.palette.background.paper,
									color: theme.palette.primary.main,
									fontWeight: 600,
								}}
							>
								Emails
							</ListSubheader>
						}
					>
						<ListItemButton
							selected={option === "email-topics"}
							component={Link}
							to={emailTopicsUrl}
						>
							<ListItemIcon>
								<EmailIcon />
							</ListItemIcon>
							<ListItemText primary="Email Topics" />
						</ListItemButton>
						<ListItemButton
							selected={option === "email-addresses"}
							component={Link}
							to={"/configuration/email-addresses"}
						>
							<ListItemIcon>
								<AlternateEmailIcon />
							</ListItemIcon>
							<ListItemText primary="Email Addresses" />
						</ListItemButton>
					</List>
				</>
			)}

			<Divider />

			<div>
				<CustomSidebar option={option} />
			</div>

			<Divider />

			<List
				subheader={
					<ListSubheader
						style={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.primary.main,
							fontWeight: 600,
						}}
					>
						Real Time
					</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "real-time/client"}
					component={Link}
					to="/configuration/real-time/client"
				>
					<ListItemIcon>
						<ChatIcon />
					</ListItemIcon>
					<ListItemText primary="Client" />
				</ListItemButton>
				<ListItemButton
					selected={option === "real-time/config"}
					component={Link}
					to="/configuration/real-time/config"
				>
					<ListItemIcon>
						<TimelineIcon />
					</ListItemIcon>
					<ListItemText primary="Config" />
				</ListItemButton>
			</List>

			<Divider />

			<List
				subheader={
					<ListSubheader
						style={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.primary.main,
							fontWeight: 600,
						}}
					>
						Query Cache
					</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "query-cache/config"}
					component={Link}
					to="/configuration/query-cache/config"
				>
					<ListItemIcon>
						<MemoryIcon />
					</ListItemIcon>
					<ListItemText primary="Config" />
				</ListItemButton>
			</List>

			<Divider />

			<List
				subheader={
					<ListSubheader
						style={{
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.primary.main,
							fontWeight: 600,
						}}
					>
						Maintenance
					</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "logs"}
					component={Link}
					to="/configuration/logs"
				>
					<ListItemIcon>
						<ViewListIcon />
					</ListItemIcon>
					<ListItemText primary="Logs" />
				</ListItemButton>
				<ListItemButton
					selected={option === "client-telemetry"}
					component={Link}
					to="/configuration/client-telemetry"
				>
					<ListItemIcon>
						<TimelineIcon />
					</ListItemIcon>
					<ListItemText primary="Client Telemetry" />
				</ListItemButton>
				<ListItemButton
					selected={option === "jobs"}
					component={Link}
					to="/configuration/jobs"
				>
					<ListItemIcon>
						<BuildIcon />
					</ListItemIcon>
					<ListItemText primary="Jobs" />
				</ListItemButton>
				<ListItemButton
					selected={option === "settings"}
					component={Link}
					to="/configuration/settings"
				>
					<ListItemIcon>
						<SettingsIcon />
					</ListItemIcon>
					<ListItemText primary="Settings" />
				</ListItemButton>
				<ListItemButton
					selected={option === "sp-executor"}
					component={Link}
					to="/configuration/sp-executor"
				>
					<ListItemIcon>
						<CodeIcon />
					</ListItemIcon>
					<ListItemText primary="SP Executor" />
				</ListItemButton>
			</List>
		</>
	);

	useEffect(() => {
		const handleListItemClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			// Check if the clicked element or its parents is a ListItem
			const listItem = target.closest(".MuiListItem-root");

			if (listItem && isMobile) {
				setDrawerOpen(false);
			}
		};

		// Add the event listener to the document
		document.addEventListener("click", handleListItemClick);

		// Cleanup
		return () => {
			document.removeEventListener("click", handleListItemClick);
		};
	}, [isMobile]);

	return (
		// Update the container div styling
		<div style={{ display: "flex", position: "relative" }}>
			<Drawer
				variant={isMobile ? "temporary" : "permanent"}
				open={drawerOpen}
				onClose={() => isMobile && setDrawerOpen(false)}
				style={{
					width: drawerOpen ? 240 : 0,
					flexShrink: 0,
				}}
				PaperProps={{
					style: {
						width: 240,
						height: "calc(100vh - 64px)",
						backgroundColor: theme.palette.background.paper,
						top: 64,
						zIndex: 1200,
					},
				}}
			>
				{renderDrawerContent()}
			</Drawer>

			<main
				style={{
					flexGrow: 1,
					padding: theme.spacing(3),
					transition: theme.transitions.create(["padding-left"], {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
					width: "100%",
				}}
			>
				<Routes>
					{routes.map((route) => (
						<Route
							key={`${route.path}`}
							path={route.path}
							element={
								<>
									{isMobile && (
										<IconButton
											onClick={() => setDrawerOpen(!drawerOpen)}
											style={{
												position: "relative",
												left: 0,
												top: -10,
												zIndex: 1100,
												borderRadius: "0 4px 4px 0",
												color: theme.palette.primary.main,
												backgroundColor: theme.palette.background.paper,
											}}
										>
											{drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
										</IconButton>
									)}
									<route.main />
								</>
							}
						/>
					))}
				</Routes>
			</main>
		</div>
	);
};

export default Configuration;
